<template>
  <div class="home">
    <b-container fluid>
      <b-row>
        <b-col cols="4">
          <h1 class="mb-3">{{$keycloak.fullName}}'s Applications</h1>
        </b-col>
        <b-col cols="8" v-if="hasRole('APPLICANT')">
          <b-button variant="primary" to="new" role="button">New Application</b-button>
        </b-col>
      </b-row>

      <div v-if="applications">
        <h2>Search</h2>
        <b-row>
          <b-col cols="4" md="4" rowspan="3">
            <form-group label="Status" label-for="status" :validator="$v.filters.statuses">
              <b-form-select
                      :options="statusOptions"
                      v-model="filters.statuses"
                      id="status"
                      multiple
              >
              </b-form-select>
            </form-group>
          </b-col>
          <b-col cols="8">
            <b-row>
              <b-col cols="4">
                <BaseTextInput v-model="filters.applicationNumber"
                               :v="$v.filters.applicationNumber"
                               label="Application Number"
                               slug="applicationNumber"
                               setId="applicationNumber"/>
              </b-col>
              <b-col cols="4">
                <BaseTextInput v-model="filters.hfid"
                               :v="$v.filters.hfid"
                               label="HFID"
                               slug="hfid"
                               setId="hfid"/>
              </b-col>
            </b-row>
            <b-row v-if="hasRole('ADMIN')">
              <b-col cols="6">
                <BaseCheckboxInput v-model="filters.assignedOnly"
                                   :v="$v.filters.assignedOnly"
                                   label="Assigned to me only"
                                   slug="assignedOnly"
                                   setId="assignedOnly"
                                   class="font-weight-normal"
                />
              </b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-row class="border-bottom pb-1">
          <b-col cols="1">
            <b-button type="button" variant="success" @click="search(true)">Search</b-button>
          </b-col>
          <b-col cols="1">
            <b-button type="button" variant="warning" @click="clear">Clear</b-button>
          </b-col>
        </b-row>
        <b-row class="mt-3">
          <b-col cols="12">
            <h2 class="green-header" v-if="hasRole('APPLICANT')">Your Applications</h2>
            <h2 class="green-header" v-if="hasRole('ADMIN')">Applications to be Reviewed</h2>
            <b-table hover small responsive show-empty
                     class="sr-caption"
                     id="applications"
                     :fields="fields"
                     :items="applications"
                     :busy="isLoading"
                     :current-page="currentPage"
                     :per-page="0"
                     primary-key="id"
                     label-sort-asc=""
                     label-sort-desc=""
                     @sort-changed="search"
                     caption="Applications table.  To sort the data in this table by a specific column, go to the appropriate column
                     header cell and use enter to sort the data ascending.  Use enter again to sort descending.">

              <template v-slot:empty="scope">
                <p>No applications found.</p>
              </template>

              <div slot="table-busy" class="text-center text-danger my-2">
                <b-spinner class="align-middle spinner-margin"></b-spinner>
                <strong>Loading, Please Wait...</strong>
              </div>

              <template v-slot:cell(lastModifiedDate)="row">
                {{momentFormat(row.item.lastModifiedDate)}}
              </template>

              <template v-slot:cell(actions)="row">
                <b-button size="sm" :to="'view/'+row.item.id" class="mr-1">
                  <span v-if="hasRole('ADMIN')">Review</span>
                  <span v-if="hasRole('APPLICANT')">View</span>
                </b-button>
                <b-button size="sm" @click="showDeleteModal(row.item)" class="mr-1" v-if="hasRole('APPLICANT') && isStatus(['DRAFT','REOPEN'], row.item)">
                  Delete Application
                </b-button>
              </template>
            </b-table>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-pagination
                    v-model="currentPage"
                    :per-page="perPage"
                    :total-rows="totalRows"
                    align="fill"
                    size="sm"
                    class="my-0"
                    @change="search(false, $event)"
            ></b-pagination>
          </b-col>
        </b-row>
        <b-row class="mt-2">
          <b-col cols="2" offset="10">
            <b-form-group
                    label="Per Page:"
                    label-size="sm"
                    label-for="perPageSelect"
                    class="custom-control-inline mr-3"
            >
              <b-form-select
                      v-model="perPage"
                      id="perPageSelect"
                      size="sm"
                      :options="pageOptions"
                      @change="search(true)"
              ></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>
      </div>
    </b-container>

    <b-modal id="delete-modal" hide-header hide-footer>
      <h2>Delete Application</h2>
      <hr/>
      <form ref="form" @submit.prevent="deleteApplication">
        You are about to delete application number {{selected.applicationNumber}}.
        <p class="mt-2">Press Confirm to delete or Cancel to return to the application review.</p>
        <div>
          <b-button @click="closeDeleteModal" style="margin-right:15px;" variant="secondary">Cancel</b-button>
          <b-button type="submit" variant="primary">Confirm</b-button>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
  import SecureLS from "secure-ls"
  import ConfigService from "@/services/ConfigService";
  import axios from 'axios'
  import axiosRetry from 'axios-retry'
  import moment from 'moment'
  import { required } from 'vuelidate/lib/validators'

  axiosRetry(axios, { retries: 3,
    retryDelay: axiosRetry.exponentialDelay  })

  export default {
    data: () => ({
      applications: [],
      fields: [
        {key: 'applicationNumber', label: 'Number'},
        {key: 'hfid', label: 'HFID'},
        {key: 'appName.programNm', label: 'Program Name'},
        {key: 'status', label: 'Status'},
        {key: 'createdByName', label: 'Submitter Name'},
        {key: 'submitter.email', label: 'Submitter Email'},
        {key: 'submitter.phone', label: 'Submitter Phone'},
        {key: 'lastModifiedDate', label: 'Last Modified'},
        {key: 'actions', label: 'Actions'}
      ],
      selected: {},
      perPage: 10,
      currentPage: 1,
      totalRows: 1,
      pageOptions: [5, 10, 15, 20],
      isLoading: false,
      statusOptions: [
        { text: "Draft", value:"DRAFT" },
        { text: "Reopened", value:"REOPEN" },
        { text: "Submitted", value:"SUBMITTED" },
        { text: "Resubmitted", value:"RE_SUBMITTED" },
        { text: "In Review", value:"IN_REVIEW" } ,
        { text: "Waiting for Payment", value:"WAITING_FOR_PAYMENT" },
        { text: "Documents Purged", value:"DOCUMENTS_PURGED" },
        { text: "Paid", value:"PAID" },
        { text: "Sent to Deposit Team", value:"SENT_TO_DEPOSIT_TEAM" },
        { text: "Sent to Credentialer", value:"SENT_TO_CREDENTIALER" }
      ],
      filters: {
        statuses: null,
        applicationNumber: null,
        hfid: null,
        assignedOnly: false
      }
    }),

    mounted() {
      if (this.$keycloak) {
        this.filters.statuses = this.getInitialStatus()
        this.search()
      }
    },

    methods: {
      clear() {
        this.filters.statuses = this.getInitialStatus()
        this.filters.applicationNumber = null
        this.filters.hfid = null
      },
      search(reset, $event) {
        if (reset === true) {
          this.currentPage = 1
        }
        let payload = {
          applicationNumber: (this.filters.applicationNumber && this.filters.applicationNumber.trim().length > 0)
                  ? this.filters.applicationNumber : null,
          hfid: (this.filters.hfid && this.filters.hfid.trim().length > 0)
                  ? this.filters.hfid : null,
          statuses: this.filters.statuses.length > 0 ? this.filters.statuses.join(",") : null,
          assignedOnly: this.filters.assignedOnly,
          page: !$event ? this.currentPage -1 : $event -1,
          size: this.perPage,
          sort:  "createDate"
        }
        axios.get(ConfigService.getApiUrl() + `/api/applications/search`, { params: payload })
                .then(response => {
                  this.applications = response.data.content
                  this.totalRows = response.data.totalElements
                })
      },
      hasRole(roles) {
        if (this.$keycloak) {
          return this.$keycloak.hasResourceRole(roles, ConfigService.getBackendClientName())
        } else {
          return false;
        }
      },
      getInitialStatus() {
        if (this.hasRole("ADMIN")) {
          return ["SUBMITTED", "RE_SUBMITTED", "IN_REVIEW", "PAID"]
        } else {
          return ["DRAFT","REOPEN","WAITING_FOR_PAYMENT","DOCUMENTS_PURGED"]
        }
      },
      momentFormat(date) {
        return moment(date).format("MM/DD/YYYY hh:mm A")
      },
      showDeleteModal(item) {
        this.selected = item
        this.$bvModal.show('delete-modal')
      },
      closeDeleteModal() {
        this.selected = {}
        this.$bvModal.hide('delete-modal')
      },
      isStatus(status, item) {
        return status.some(s => s === item.status)
      },
      deleteApplication() {
        axios.delete(ConfigService.getApiUrl() + `/api/applications/` + this.selected.id)
          .then(response => {
            this.showRootToast("Deleted", "Application has been deleted", "success")
            this.closeDeleteModal()
            this.loadApplications()
          }).catch(err => {
            let errorList = Object.values(err.response.data)
            this.showRootToast("Error", this.createErrorToast(errorList),
                  "danger")
            this.closeDeleteModal()
        })
      },
      createErrorToast(errors) {
        const h = this.$createElement
        const errNodes = h(
                'div',
                [
                  h('p','Application contains errors, please fix and resubmit'),
                  h('ul', [
                    errors.map(e => {
                      return h('li', {attrs:{class: 'circle-list ml-4'}}, e)
                    })
                  ])
                ]
        )
        return errNodes
      },
      showRootToast(title, message, type) {
        this.$root.$bvToast.toast(message, {
          title: title,
          toaster: 'b-toaster-top-center',
          solid: true,
          variant: type,
          appendToast: true,
          autoHideDelay: 5000,
        })
        this.toastFocus()
      },
      toastFocus() {
        setTimeout(() => {
          let elem = document.getElementsByClassName("toast")
          elem = elem[elem.length-1]
          elem.focus()
        }, 100)
      },
    },
    validations() {
      return {
        filters: {
          statuses: {required: false},
          applicationNumber: {required: false},
          hfid: {required: false},
          assignedOnly: { required },
        }
      }
    },
    created() {
      if (this.$keycloak && this.$keycloak.fullName) {
        document.title = this.$keycloak.fullName + "'s Applications - Facility and Provider Licensing System"
      } else {
        document.title = "My Applications - Facility and Provider Licensing System"
      }
    }
  }
</script>